import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  VIEW_MODE,
  getViewportClass,
  readViewPreference,
  writeViewPreference,
} from '../utils/viewPreference'

const getBrowserStorage = () => {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

const getInitialViewportClass = () => (
  typeof window === 'undefined' ? getViewportClass(1024) : getViewportClass(window.innerWidth)
)

const defaultModeFor = (viewportClass) => (
  viewportClass === 'mobile' ? VIEW_MODE.CARDS : VIEW_MODE.TABLE
)

const useResponsiveViewPreference = (family) => {
  const [viewportClass, setViewportClass] = useState(getInitialViewportClass)
  const [mode, setMode] = useState(() => {
    const initialViewportClass = getInitialViewportClass()
    return readViewPreference({
      family,
      viewportClass: initialViewportClass,
      storage: typeof window === 'undefined' ? null : getBrowserStorage(),
    }) || defaultModeFor(initialViewportClass)
  })

  useEffect(() => {
    const handleResize = () => {
      const nextViewportClass = getViewportClass(window.innerWidth)
      setViewportClass((current) => current === nextViewportClass ? current : nextViewportClass)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const savedMode = readViewPreference({ family, viewportClass, storage: getBrowserStorage() })
    setMode(savedMode || defaultModeFor(viewportClass))
  }, [family, viewportClass])

  const selectMode = useCallback((nextMode) => {
    if (nextMode !== VIEW_MODE.TABLE && nextMode !== VIEW_MODE.CARDS) return
    setMode(nextMode)
    writeViewPreference({
      family,
      viewportClass,
      mode: nextMode,
      storage: getBrowserStorage(),
    })
  }, [family, viewportClass])

  return useMemo(() => ({ mode, selectMode, viewportClass }), [mode, selectMode, viewportClass])
}

export default useResponsiveViewPreference
